import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// Import default cycles as a fallback in case server-side data fails to load
import { breakthroughCycles as defaultCycles, welcomeContent } from '@/data/playbookCyclesData';
import { useToast } from '@/components/ui/use-toast';
import { GrowthPhase, BreakthroughCycle } from '@/types/playbook';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Define types for database records
interface TaskRecord {
  id: string;
  user_id: string;
  project_id: string;
  strategy_id?: string;
  task_id?: string;
  phase_id: string;
  completed: boolean;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

interface ProgressRecord {
  id: string;
  user_id: string;
  project_id: string;
  current_phase_id: string | null;
  current_cycle_id: string | null;
  has_seen_intro: boolean;
  last_active_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Helper function to find a phase across all cycles
const findPhaseById = (phaseId: string, cycles: BreakthroughCycle[]): { cycle: BreakthroughCycle; phase: GrowthPhase } | null => {
  if (!phaseId) return null;
  
  for (const cycle of cycles) {
    if (!cycle || !cycle.phases) continue;
    
    const phase = cycle.phases.find(p => p && p.id === phaseId);
    if (phase) {
      return { cycle, phase };
    }
  }
  
  return null;
};

// Simple cache to store progress data
interface ProgressCache {
  timestamp: number;
  activeCycleId: string | null;
  activePhaseId: string | null;
  completedTasks: Record<string, boolean>;
  hasSeenIntro: boolean;
  cycles: BreakthroughCycle[];
}

// Create a global cache object to persist between component mounts
const progressCache: Record<string, ProgressCache> = {};

interface PlaybookProgressState {
  completedTasks: Record<string, boolean>;
  activeCycleId: string | null;
  activePhaseId: string | null; 
  hasSeenIntro: boolean;
  isLoading: boolean;
  cycles: BreakthroughCycle[];
}

interface UsePlaybookProgressReturn extends PlaybookProgressState {
  toggleTask: (taskId: string, phaseId: string, isCompleted: boolean) => Promise<void>;
  setActiveCycle: (cycleId: string) => Promise<void>;
  setActivePhase: (cycleId: string, phaseId: string) => Promise<void>;
  setActiveWelcome: () => Promise<void>;
  setHasSeenIntro: (seen: boolean) => Promise<void>;
  calculateTotalProgress: () => { completed: number; total: number; percentage: number };
  calculatePhaseProgress: (phaseId: string) => { completedCount: number; totalTasks: number; percentage: number };
  calculateCycleProgress: (cycleId: string) => { completedTasks: number; totalTasks: number; completedPhases: number; totalPhases: number };
  activeCycle: BreakthroughCycle | null;
  activePhase: GrowthPhase | null;
  invalidateCache: () => void;
  checkSupabaseConnection: () => Promise<{ connected: boolean; data?: any; error?: any }>;
}

export const usePlaybookProgress = (projectId?: string): UsePlaybookProgressReturn => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [state, setState] = useState<PlaybookProgressState>({
    completedTasks: {},
    activeCycleId: null,
    activePhaseId: null,
    hasSeenIntro: false,
    isLoading: true,
    cycles: defaultCycles, // Use default cycles initially, will be replaced with server data
  });
  
  // Cache key based on user ID and project ID
  const cacheKey = useRef<string>(`${user?.id || 'guest'}-${projectId || 'default'}`);

  // Add a Supabase connection check function
  const checkSupabaseConnection = useCallback(async () => {
    try {
      // Instead of using a custom RPC function, use a simpler query to check connection
      const { data, error } = await supabase
        .from('playbook_progress')
        .select('count', { count: 'exact', head: true });
      
      return { connected: !error, data, error };
    } catch (error) {
      console.error('Supabase connection check failed:', error);
      return { connected: false, error };
    }
  }, []);

  // Computed properties for active cycle and phase
  const activeCycle = state.activeCycleId 
    ? state.cycles.find(c => c.id === state.activeCycleId) || null 
    : null;
    
  const activePhase = state.activePhaseId 
    ? findPhaseById(state.activePhaseId, state.cycles)?.phase || null 
    : null;

  // Update or create playbook_progress record
  const updateProgressRecord = useCallback(async (data: Record<string, any>) => {
    if (!user?.id || !projectId) return;

    try {
      // Check if record exists
      const { data: existing, error: checkError } = await supabase
        .from('playbook_progress')
        .select('id')
        .eq('user_id', user.id)
        .eq('project_id', projectId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      // Create a database-compatible object with all valid fields
      const dbCompatibleData: Record<string, any> = {};
      
      // Include all fields that exist in the database schema
      if ('current_cycle_id' in data) dbCompatibleData.current_cycle_id = data.current_cycle_id;
      if ('current_phase_id' in data) dbCompatibleData.current_phase_id = data.current_phase_id;
      if ('has_seen_intro' in data) dbCompatibleData.has_seen_intro = data.has_seen_intro;
      
      // Add updated timestamp
      dbCompatibleData.updated_at = new Date().toISOString();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('playbook_progress')
          .update(dbCompatibleData)
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('playbook_progress')
          .insert({
            user_id: user.id,
            project_id: projectId,
            current_cycle_id: data.current_cycle_id || null,
            current_phase_id: data.current_phase_id || null,
            has_seen_intro: data.has_seen_intro || false,
          });

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error updating progress record:', error);
      toast({
        title: 'Error saving progress',
        description: 'Failed to save your progress. Please try again.',
        variant: 'destructive',
      });
    }
  }, [user?.id, projectId, toast]);

  // Load progress data from Supabase
  const loadProgressData = useCallback(async () => {
    if (!user?.id || !projectId) return;

    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      // First check if Supabase is connected
      const connectionCheck = await checkSupabaseConnection();
      if (!connectionCheck.connected) {
        console.error('Supabase connection failed, using default values');
        setState(prev => ({ ...prev, isLoading: false }));
        toast({
          title: 'Connection error',
          description: 'Failed to connect to the database. Some features may not work properly.',
          variant: 'destructive',
        });
        return;
      }
      
      // Check if we have valid cached data
      const cachedData = progressCache[cacheKey.current];
      const now = Date.now();
      
      if (cachedData && (now - cachedData.timestamp) < CACHE_DURATION) {
        // Use cached data if it's still valid
        setState(prev => ({
          ...prev,
          activeCycleId: cachedData.activeCycleId,
          activePhaseId: cachedData.activePhaseId,
          completedTasks: cachedData.completedTasks,
          hasSeenIntro: cachedData.hasSeenIntro,
          isLoading: false,
          cycles: cachedData.cycles || defaultCycles // Use cached cycles if available
        }));
        console.log('Using cached playbook progress data');
        return;
      }

      // NOTE: The 'get_playbook_cycles' RPC function no longer exists
      // Use the default cycles data directly instead of trying to fetch from server
      const cycles = defaultCycles;
      
      // Log that we're using static data
      console.log('Using static playbook data instead of fetching from Supabase');

      // Fetch progress data
      const { data: progressData, error: progressError } = await supabase
        .from('playbook_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('project_id', projectId)
        .single();

      if (progressError && progressError.code !== 'PGRST116') { // PGRST116 is "not found"
        throw progressError;
      }

      // Fetch completed tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from('playbook_tasks')
        .select('*')
        .eq('user_id', user.id)
        .eq('project_id', projectId);

      if (tasksError) throw tasksError;

      // Process tasks data
      const completedTasks: Record<string, boolean> = {};
      tasksData?.forEach(task => {
        // Cast to our TaskRecord type
        const typedTask = task as unknown as TaskRecord;
        // Handle both old strategy_id and new task_id field names
        const taskId = typedTask.task_id || typedTask.strategy_id;
        if (taskId) {
          completedTasks[taskId] = typedTask.completed;
        }
      });

      // Extract progress data
      let hasSeenIntro = false;
      let activeCycleId: string | null = null;
      let activePhaseId: string | null = null;

      if (progressData) {
        // Cast to our ProgressRecord type
        const typedProgressData = progressData as unknown as ProgressRecord;
        
        // Extract values with fallbacks
        hasSeenIntro = !!typedProgressData.has_seen_intro;
        activePhaseId = typedProgressData.current_phase_id || null;
        activeCycleId = typedProgressData.current_cycle_id || null;
        
        // Logic to infer missing phase/cycle data
        if (activePhaseId && !activeCycleId) {
          // If we have a phase but no cycle, try to find the cycle that contains this phase
          const result = findPhaseById(activePhaseId, cycles);
          if (result) {
            activeCycleId = result.cycle.id;
            // Update the DB with the inferred cycle ID
            updateProgressRecord({ current_cycle_id: activeCycleId })
              .catch(err => console.error('Error updating inferred cycle:', err));
          }
        } else if (!activePhaseId && activeCycleId) {
          // If we have a cycle but no phase, default to the first phase of that cycle
          const cycle = cycles.find(c => c.id === activeCycleId);
          if (cycle && cycle.phases.length > 0) {
            activePhaseId = cycle.phases[0].id;
            // Update the DB with the inferred phase ID
            updateProgressRecord({ current_phase_id: activePhaseId })
              .catch(err => console.error('Error updating inferred phase:', err));
          }
        } else if (!activePhaseId && !activeCycleId && cycles.length > 0) {
          // If we have neither, default to the first cycle's first phase
          const firstCycle = cycles[0];
          activeCycleId = firstCycle.id;
          if (firstCycle.phases.length > 0) {
            activePhaseId = firstCycle.phases[0].id;
          }
        }
      }

      // Update the local state with all the data we've gathered
      setState({
        completedTasks,
        activeCycleId,
        activePhaseId,
        hasSeenIntro,
        isLoading: false,
        cycles,
      });

      // Cache the progress data for future use
      progressCache[cacheKey.current] = {
        timestamp: now,
        activeCycleId,
        activePhaseId,
        completedTasks,
        hasSeenIntro,
        cycles,
      };

    } catch (error) {
      console.error('Error loading playbook progress:', error);
      // Update state to not loading even on error
      setState(prev => ({ ...prev, isLoading: false }));
      toast({
        title: 'Error',
        description: 'Failed to load your playbook progress. Please try again.',
        variant: 'destructive',
      });
    }
  }, [user?.id, projectId, toast, updateProgressRecord, checkSupabaseConnection]);

  // Update cache with new state
  const updateCache = useCallback((
    updatedState: Partial<Omit<PlaybookProgressState, 'isLoading'>>
  ) => {
    const cachedData = progressCache[cacheKey.current];
    if (cachedData) {
      progressCache[cacheKey.current] = {
        ...cachedData,
        ...updatedState,
        timestamp: Date.now()
      };
    }
  }, []);

  // Initial data load
  useEffect(() => {
    loadProgressData();
  }, [loadProgressData]);
  
  // Update cacheKey if user or projectId changes
  useEffect(() => {
    cacheKey.current = `${user?.id || 'guest'}-${projectId || 'default'}`;
  }, [user?.id, projectId]);

  // Invalidate cache function for when we need fresh data
  const invalidateCache = useCallback(() => {
    delete progressCache[cacheKey.current];
    loadProgressData();
  }, [loadProgressData]);

  // Toggle task completion
  const toggleTask = useCallback(async (
    taskId: string, 
    phaseId: string, 
    isCompleted: boolean
  ) => {
    if (!user?.id || !projectId) return;

    try {
      // Update local state first for immediate UI feedback
      setState(prev => ({
        ...prev,
        completedTasks: {
          ...prev.completedTasks,
          [taskId]: isCompleted
        }
      }));
      
      // Update cache
      updateCache({
        completedTasks: {
          ...state.completedTasks,
          [taskId]: isCompleted
        }
      });

      // Check if task exists in the database - use raw query approach
      let existingTask = null;
      let checkError = null;
      
      try {
        // Use any type to bypass TypeScript's deep instantiation issue
        const response = await (supabase as any)
          .from('playbook_tasks')
          .select('id')
          .eq('user_id', user.id)
          .eq('project_id', projectId)
          .eq('strategy_id', taskId)
          .single();
          
        existingTask = response.data;
        checkError = response.error;
      } catch (err) {
        checkError = err;
      }

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingTask) {
        // Update existing task
        const updateResponse = await (supabase as any)
          .from('playbook_tasks')
          .update({
            completed: isCompleted,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingTask.id);
          
        if (updateResponse.error) throw updateResponse.error;
      } else {
        // Create new task record with safe field structure
        try {
          // First attempt with a compatible structure that works with both schemas
          const insertResponse = await (supabase as any)
            .from('playbook_tasks')
            .insert({
              user_id: user.id,
              project_id: projectId,
              strategy_id: taskId,
              phase_id: phaseId,
              completed: isCompleted
            });
            
          if (insertResponse.error) throw insertResponse.error;
        } catch (error) {
          console.error('Error creating task record:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error toggling task:', error);
      // Revert the state change if there was an error
      setState(prev => ({
        ...prev,
        completedTasks: {
          ...prev.completedTasks,
          [taskId]: !isCompleted
        }
      }));
      
      toast({
        title: 'Error saving progress',
        description: 'Failed to save your task progress. Please try again.',
        variant: 'destructive',
      });
    }
  }, [user?.id, projectId, state.completedTasks, updateCache, toast]);

  // Set the active phase (and its parent cycle)
  const setActivePhase = useCallback(async (cycleId: string, phaseId: string): Promise<void> => {
    if (!user?.id || !projectId) return;

    try {
      // Validate the cycle and phase exist
      const cycleExists = state.cycles.some(c => c.id === cycleId);
      
      let phaseExists = false;
      if (cycleExists) {
        const cycle = state.cycles.find(c => c.id === cycleId);
        phaseExists = cycle?.phases.some(p => p.id === phaseId) || false;
      }
      
      if (!cycleExists || !phaseExists) {
        console.error(`Invalid cycle (${cycleId}) or phase (${phaseId})`);
        return;
      }

      // Update the database
      await updateProgressRecord({
        current_cycle_id: cycleId,
        current_phase_id: phaseId,
      });

      // Update local state immediately
      setState(prev => ({
        ...prev,
        activeCycleId: cycleId,
        activePhaseId: phaseId,
      }));

      // Invalidate cache for this key
      delete progressCache[cacheKey.current];
    } catch (error) {
      console.error('Error setting active phase:', error);
      toast({
        title: 'Error',
        description: 'Failed to update your progress. Please try again.',
        variant: 'destructive',
      });
    }
  }, [projectId, user?.id, updateProgressRecord, toast, state.cycles]);

  // Set just the active cycle (usually first phase of that cycle)
  const setActiveCycle = useCallback(async (cycleId: string): Promise<void> => {
    if (!user?.id || !projectId) return;

    try {
      // Find the cycle and get its first phase
      const cycle = state.cycles.find(c => c.id === cycleId);
      
      if (!cycle || cycle.phases.length === 0) {
        console.error(`Invalid cycle (${cycleId}) or cycle has no phases`);
        return;
      }

      const firstPhase = cycle.phases[0];

      // Update the database with both cycle and phase
      await updateProgressRecord({
        current_cycle_id: cycleId,
        current_phase_id: firstPhase.id,
      });

      // Update local state immediately
      setState(prev => ({
        ...prev,
        activeCycleId: cycleId,
        activePhaseId: firstPhase.id,
      }));

      // Invalidate cache for this key
      delete progressCache[cacheKey.current];
    } catch (error) {
      console.error('Error setting active cycle:', error);
      toast({
        title: 'Error',
        description: 'Failed to update your progress. Please try again.',
        variant: 'destructive',
      });
    }
  }, [projectId, user?.id, updateProgressRecord, toast, state.cycles]);

  // Set active to welcome screen
  const setActiveWelcome = useCallback(async (): Promise<void> => {
    if (!user?.id || !projectId) return;

    try {
      // Update the database
      await updateProgressRecord({
        current_cycle_id: null,
        current_phase_id: null,
      });

      // Update local state immediately
      setState(prev => ({
        ...prev,
        activeCycleId: null,
        activePhaseId: null,
      }));

      // Invalidate cache for this key
      delete progressCache[cacheKey.current];
    } catch (error) {
      console.error('Error returning to welcome screen:', error);
      toast({
        title: 'Error',
        description: 'Failed to update your progress. Please try again.',
        variant: 'destructive',
      });
    }
  }, [projectId, user?.id, updateProgressRecord, toast]);

  // Set has seen intro
  const setHasSeenIntro = useCallback(async (seen: boolean) => {
    setState(prev => ({
      ...prev,
      hasSeenIntro: seen
    }));
    
    updateCache({
      hasSeenIntro: seen
    });
    
    await updateProgressRecord({
      has_seen_intro: seen
    });
  }, [updateCache, updateProgressRecord]);

  // Define simpler progress type
  type ProgressResult = {
    completedCount: number;
    totalTasks: number;
    percentage: number;
  };

  type CycleProgressResult = {
    completedTasks: number;
    totalTasks: number;
    completedPhases: number;
    totalPhases: number;
  };

  // Calculate progress for a specific phase
  const calculatePhaseProgress = useCallback((phaseId: string): ProgressResult => {
    // First make sure we have a valid phaseId
    if (!phaseId) {
      return { completedCount: 0, totalTasks: 0, percentage: 0 };
    }

    const result = findPhaseById(phaseId, state.cycles);
    if (!result || !result.phase) {
      return { completedCount: 0, totalTasks: 0, percentage: 0 };
    }
    
    const { phase } = result;
    
    // Check if tasks array exists and is not empty
    if (!phase.tasks || phase.tasks.length === 0) {
      return { completedCount: 0, totalTasks: 0, percentage: 0 };
    }
    
    const totalTasks = phase.tasks.length;
    
    // Safe access completedTasks with fallback for undefined
    const completedTasks = state.completedTasks || {};
    
    // Make sure we don't process invalid tasks
    const validTasks = phase.tasks.filter(task => task && task.id);
    
    const completedCount = validTasks.filter(task => 
      completedTasks[task.id] === true
    ).length;
    
    // Avoid division by zero
    const percentage = totalTasks > 0 
      ? Math.round((completedCount / totalTasks) * 100)
      : 0;
    
    return { completedCount, totalTasks, percentage };
  }, [state.completedTasks, state.cycles]);

  // Calculate progress for a specific cycle
  const calculateCycleProgress = useCallback((cycleId: string): CycleProgressResult => {
    // Make sure we have a valid cycleId
    if (!cycleId) {
      return { completedTasks: 0, totalTasks: 0, completedPhases: 0, totalPhases: 0 };
    }
    
    // Find the cycle by ID
    const cycle = state.cycles.find(c => c.id === cycleId);
    if (!cycle) {
      return { completedTasks: 0, totalTasks: 0, completedPhases: 0, totalPhases: 0 };
    }
    
    const totalPhases = cycle.phases?.length || 0;
    
    // Initialize counters
    let completedTaskCount = 0;
    let totalTaskCount = 0;
    let completedPhaseCount = 0;
    
    // Process each phase individually
    if (cycle.phases && cycle.phases.length > 0) {
      for (const phase of cycle.phases) {
        if (!phase || !phase.tasks) continue;
        
        const tasks = phase.tasks || [];
        const totalPhaseTasks = tasks.length;
        let completedPhaseTasks = 0;
        
        // Count completed tasks with safe access to state.completedTasks
        const completedTasks = state.completedTasks || {};
        for (const task of tasks) {
          if (!task || !task.id) continue;
          
          if (completedTasks[task.id] === true) {
            completedPhaseTasks++;
          }
        }
        
        // Update counters
        totalTaskCount += totalPhaseTasks;
        completedTaskCount += completedPhaseTasks;
        
        // Check if phase is completed
        if (totalPhaseTasks > 0 && completedPhaseTasks === totalPhaseTasks) {
          completedPhaseCount++;
        }
      }
    }
    
    return { 
      completedTasks: completedTaskCount, 
      totalTasks: totalTaskCount, 
      completedPhases: completedPhaseCount, 
      totalPhases 
    };
  }, [state.completedTasks, state.cycles]);

  // Calculate total progress across all cycles
  const calculateTotalProgress = useCallback(() => {
    try {
      let totalTaskCount = 0;
      let completedTaskCount = 0;
      
      // Make sure defaultCycles is a valid array
      if (!state.cycles || !Array.isArray(state.cycles)) {
        return { completed: 0, total: 0, percentage: 0 };
      }
      
      // Process each cycle
      for (const cycle of state.cycles) {
        if (!cycle || !cycle.id) continue;
        
        try {
          const progress = calculateCycleProgress(cycle.id);
          totalTaskCount += progress.totalTasks || 0;
          completedTaskCount += progress.completedTasks || 0;
        } catch (cycleError) {
          console.error(`Error calculating progress for cycle ${cycle.id}:`, cycleError);
          // Continue processing other cycles
        }
      }
      
      const percentage = totalTaskCount > 0 
        ? Math.round((completedTaskCount / totalTaskCount) * 100) 
        : 0;
        
      return { 
        completed: completedTaskCount, 
        total: totalTaskCount, 
        percentage 
      };
    } catch (error) {
      console.error('Error calculating total progress:', error);
      return { completed: 0, total: 0, percentage: 0 };
    }
  }, [calculateCycleProgress, state.cycles]);

  return {
    ...state,
    toggleTask,
    setActiveCycle,
    setActivePhase,
    setActiveWelcome,
    setHasSeenIntro,
    calculateTotalProgress,
    calculatePhaseProgress,
    calculateCycleProgress,
    activeCycle,
    activePhase,
    invalidateCache,
    checkSupabaseConnection
  };
}; 